import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { BACKEND_URL } from '../app.config';
import { ListResult } from '../api/data-model';
import { EmailTemplate } from '../api/email';

@Injectable({
  providedIn: 'root'
})
export class EmailTemplateService {
  private readonly PAGE_SIZE = 1000;

  constructor(private readonly http: HttpClient, @Inject(BACKEND_URL) private readonly baseUrl: string) { }

  getList(sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<EmailTemplate>> {
    return this.http.get<ListResult<EmailTemplate>>(this.baseUrl + '/api/app/email-template', {
      params: {
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getAll(sorting: string = ''): Observable<EmailTemplate[]> {
    return this.getList(sorting, 0, this.PAGE_SIZE).pipe(
      switchMap((firstResult: ListResult<EmailTemplate>) => {
        const allItems = [...firstResult.items];
        const totalCount = firstResult.totalCount;

        // If all items are loaded in the first request
        if (totalCount <= this.PAGE_SIZE) {
          return of(allItems);
        }

        // Calculate how many additional requests are needed
        const additionalRequests: Observable<ListResult<EmailTemplate>>[] = [];
        let skipCount = this.PAGE_SIZE;

        while (skipCount < totalCount) {
          additionalRequests.push(
            this.getList(sorting, skipCount, this.PAGE_SIZE)
          );
          skipCount += this.PAGE_SIZE;
        }

        // Execute all additional requests in parallel
        if (additionalRequests.length === 0) {
          return of(allItems);
        }

        return forkJoin(additionalRequests).pipe(
          map((results: ListResult<EmailTemplate>[]) => {
            results.forEach(result => {
              allItems.push(...result.items);
            });
            return allItems;
          })
        );
      })
    );
  }

  addEmailTemplate(emailTemplate: EmailTemplate): Observable<EmailTemplate> {
    return this.http.post<EmailTemplate>(`${this.baseUrl}/api/app/email-template`, emailTemplate);
  }

  getEmailTemplate(id: string): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(this.baseUrl + '/api/app/email-template/' + id);
  }
  getProcessTempelateCount(id: string): Observable<any> {
    return this.http.get<EmailTemplate>(this.baseUrl + '/api/app/email-template/' + id + '/process-template-id-by-email-tempalte-id/');
  }

  updateEmailTemplate(id: string, emailTemplate: EmailTemplate): Observable<EmailTemplate> {
    return this.http.put<EmailTemplate>(`${this.baseUrl}/api/app/email-template/${id}`, emailTemplate);
  }

  uploadEmailAttachment(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.baseUrl}/api/app/email-template/upload-email`, formData);
  }
}
